// Free Diet Plan & Routine — shared, client-safe domain model.
//
// Everything here is deterministic: track definitions, the eligibility gate,
// BMI/calorie maths and the calorie clamp. The AI only fills in *meals* and
// *routine text*; every number that matters is computed or clamped in this
// file so a model can never hand a visitor an unsafe target.

export const DIET_TRACKS = ["pcos", "fatty-liver", "wellness"] as const;
export type DietTrack = (typeof DIET_TRACKS)[number];

export type TrackMeta = {
  key: DietTrack;
  label: string;
  blurb: string;
  productHandle: "ova-balance" | "liver-biome-plus" | "mag-absorb-pro";
  productTitle: string;
  productWhy: string;
};

export const TRACKS: Record<DietTrack, TrackMeta> = {
  pcos: {
    key: "pcos",
    label: "PCOS / hormonal balance",
    blurb: "Insulin-first eating for irregular cycles, acne, hair fall and weight that won't move.",
    productHandle: "ova-balance",
    productTitle: "Ova Balance",
    productWhy:
      "40:1 Myo:D-Chiro inositol with Berberine HCl and Quatrefolic® L-5-MTHF — the actives most studied alongside a PCOS diet.",
  },
  "fatty-liver": {
    key: "fatty-liver",
    label: "Fatty liver (NAFLD)",
    blurb: "Lower the fructose and refined-carb load, raise protein, fibre and choline.",
    productHandle: "liver-biome-plus",
    productTitle: "Liver Biome Plus",
    productWhy:
      "Liposomal Curcumin, Silymarin and ALA plus NAC and a multi-strain biome blend, supporting the gut-liver axis your diet is already working on.",
  },
  wellness: {
    key: "wellness",
    label: "General wellness — sleep, stress, energy",
    blurb: "Steadier energy through the day and a nervous system that actually switches off at night.",
    productHandle: "mag-absorb-pro",
    productTitle: "Mag Absorb Pro",
    productWhy:
      "Four magnesium forms including Aquamin® and bisglycinate, for sleep quality, muscle recovery and calm.",
  },
};

export const DIET_TYPES = ["vegetarian", "eggetarian", "non-vegetarian", "vegan", "jain"] as const;
export type DietType = (typeof DIET_TYPES)[number];

export const REGIONS = ["north", "south", "east", "west", "northeast"] as const;
export type Region = (typeof REGIONS)[number];

export const ACTIVITY_LEVELS = ["sedentary", "light", "moderate", "active"] as const;
export type ActivityLevel = (typeof ACTIVITY_LEVELS)[number];

/** Pre-existing conditions asked in the intake. Some are hard gates. */
export const CONDITIONS = [
  { key: "none", label: "None of these" },
  { key: "thyroid", label: "Thyroid (hypo / hyper)" },
  { key: "diabetes_t2", label: "Type 2 diabetes or prediabetes" },
  { key: "diabetes_t1", label: "Type 1 diabetes" },
  { key: "hypertension", label: "High blood pressure" },
  { key: "kidney", label: "Kidney disease" },
  { key: "liver", label: "Diagnosed liver disease" },
  { key: "heart", label: "Heart condition" },
  { key: "gi", label: "IBS / IBD / acidity" },
  { key: "pregnant", label: "Pregnant or breastfeeding" },
  { key: "eating_disorder", label: "History of an eating disorder" },
  { key: "cancer", label: "Cancer treatment (current or recent)" },
] as const;

export type ConditionKey = (typeof CONDITIONS)[number]["key"];

export type DietIntake = {
  track: DietTrack;
  name: string;
  email: string;
  phone: string;
  gender: "female" | "male" | "other";
  age: number;
  heightCm: number;
  weightKg: number;
  dietType: DietType;
  region: Region;
  activity: ActivityLevel;
  sleepHours: number;
  conditions: ConditionKey[];
  medications: string;
  allergies: string;
  /** Track-specific free text: cycle regularity, alcohol, main concern. */
  trackNote: string;
  consentMarketing: boolean;
  /** Per-channel promotional opt-ins; each starts unticked. */
  consentChannels?: { email?: boolean; whatsapp?: boolean };
};

/* ------------------------------------------------------------------ */
/* Deterministic maths                                                 */
/* ------------------------------------------------------------------ */

export function bmi(heightCm: number, weightKg: number): number {
  const m = heightCm / 100;
  if (!m) return 0;
  return Math.round((weightKg / (m * m)) * 10) / 10;
}

export function bmiBand(value: number): string {
  if (value < 18.5) return "Below the healthy range";
  if (value < 23) return "Healthy range (Asian-Indian cut-off)";
  if (value < 25) return "Above the healthy range";
  if (value < 30) return "Overweight (Asian-Indian cut-off)";
  return "Obese range";
}

const ACTIVITY_FACTOR: Record<ActivityLevel, number> = {
  sedentary: 1.3,
  light: 1.45,
  moderate: 1.6,
  active: 1.75,
};

/** Absolute floors. The model's numbers are clamped to these, never trusted. */
export const CALORIE_FLOOR = { female: 1200, male: 1500, other: 1200 } as const;

export function calorieTarget(intake: DietIntake): { low: number; high: number } {
  const s = intake.gender === "male" ? 5 : -161;
  const bmr = 10 * intake.weightKg + 6.25 * intake.heightCm - 5 * intake.age + s;
  const tdee = bmr * ACTIVITY_FACTOR[intake.activity];
  // Gentle deficit only when BMI is above the healthy range.
  const overweight = bmi(intake.heightCm, intake.weightKg) >= 23;
  const centre = overweight ? tdee - 400 : tdee;
  const floor = CALORIE_FLOOR[intake.gender];
  const low = Math.max(floor, Math.round((centre - 100) / 25) * 25);
  const high = Math.max(low + 150, Math.round((centre + 100) / 25) * 25);
  return { low, high };
}

export function proteinTargetG(intake: DietIntake): number {
  const perKg = intake.conditions.includes("kidney") ? 0.8 : 1.2;
  return Math.round(intake.weightKg * perKg);
}

/** Clamp any calorie number the model produced back inside our own range. */
export function clampCalories(value: number, intake: DietIntake): number {
  const { low, high } = calorieTarget(intake);
  if (!Number.isFinite(value)) return low;
  return Math.min(high, Math.max(low, Math.round(value)));
}

/* ------------------------------------------------------------------ */
/* Eligibility gate                                                    */
/* ------------------------------------------------------------------ */

export type GateResult =
  | { ok: true }
  | { ok: false; reason: string; message: string };

const GATE_CONDITIONS: Partial<Record<ConditionKey, string>> = {
  pregnant:
    "Nutrition during pregnancy and breastfeeding needs to be supervised by your obstetrician or a clinical dietitian — a generated plan isn't safe here.",
  eating_disorder:
    "With a history of disordered eating, structured calorie plans can do more harm than good. Please work with a clinician you trust.",
  diabetes_t1:
    "Type 1 diabetes needs carbohydrate planning matched to your insulin, which only your treating team can do safely.",
  kidney:
    "Kidney disease changes protein, potassium and fluid targets significantly. Please get a plan from your nephrology team.",
  cancer:
    "During or shortly after cancer treatment, nutrition should be planned with your oncology team.",
};

export function checkEligibility(intake: DietIntake): GateResult {
  if (intake.age < 18) {
    return {
      ok: false,
      reason: "under_18",
      message:
        "This plan is built for adults. For anyone under 18, please speak with a paediatrician or a registered dietitian.",
    };
  }
  if (intake.age > 85) {
    return {
      ok: false,
      reason: "over_85",
      message:
        "Above 85, nutrition targets need individual medical review. Please speak with your doctor.",
    };
  }
  const value = bmi(intake.heightCm, intake.weightKg);
  if (value > 0 && value < 16) {
    return {
      ok: false,
      reason: "bmi_low",
      message:
        "Your BMI is in a range where a self-serve plan isn't appropriate. Please see a doctor or clinical dietitian first.",
    };
  }
  for (const key of intake.conditions) {
    const message = GATE_CONDITIONS[key];
    if (message) return { ok: false, reason: `condition_${key}`, message };
  }
  return { ok: true };
}

/* ------------------------------------------------------------------ */
/* Generated plan shape                                                */
/* ------------------------------------------------------------------ */

export type PlanMeal = { label: string; items: string; note?: string };
export type PlanDay = { day: string; meals: PlanMeal[] };

export type GeneratedPlan = {
  headline: string;
  snapshot: {
    bmi: number;
    bmiBand: string;
    calorieLow: number;
    calorieHigh: number;
    proteinG: number;
    focus: string[];
  };
  days: PlanDay[];
  routine: { time: string; action: string }[];
  favour: string[];
  limit: string[];
  grocery: string[];
  /** Injected by our code from the track → SKU map, never written by the model. */
  support: { title: string; handle: string; why: string };
  disclaimer: string;
};

export const PLAN_DISCLAIMER =
  "This plan is general nutrition education, not medical advice, diagnosis or treatment. It does not replace your doctor or a registered dietitian. Stop and seek medical advice if you feel unwell, and always tell your doctor about anything you take alongside prescribed medication.";

export const PROMPT_VERSION = "diet-plan-v1";
